export const mobileEditorCss = `
  .mobile-editor * {
    -webkit-tap-highlight-color: transparent;
  }

  /* Media upload failure banner. Pinned to the visual viewport's top so it is
     visible with the keyboard open, and above the toolbar's z-index. */
  .mobile-editor__error {
    position: fixed;
    top: calc(var(--vvtop, 0px) + 8px);
    left: 8px;
    right: 8px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid #f0b4b4;
    background-color: #fdecec;
    color: #8a1f1f;
    font-size: 13px;
    line-height: 1.35;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    z-index: 3100;
  }
  .mobile-editor__error-text {
    flex: 1;
  }
  .mobile-editor__error-dismiss {
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: inherit;
    font-size: 18px;
    line-height: 1;
    padding: 0 2px;
    cursor: pointer;
  }

  /* Upload / paste loader overlay */
  .mobile-editor__busy {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.6);
    z-index: 3000;
  }
  .mobile-editor__spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #d0d7de;
    border-top-color: #00a0e1;
    border-radius: 50%;
    animation: mobile-editor-spin 0.8s linear infinite;
  }
  @keyframes mobile-editor-spin {
    to { transform: rotate(360deg); }
  }

  /* Stand-in caret: WebKit stops painting the real one deep inside the scroller (iOS). */
  .mobile-editor__caret-proxy {
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: 1em;
    z-index: 90;
    border-radius: 1px;
    background: #1a1a1a;
    pointer-events: none;
    opacity: 0;
    transform: translate3d(-10000px, -10000px, 0);
  }
  .mobile-editor__caret-proxy--visible {
    opacity: 1;
    animation: mobile-editor-caret-blink 1s steps(1, end) infinite;
  }
  @keyframes mobile-editor-caret-blink {
    0%, 45%, 100% { opacity: 1; }
    50%, 95% { opacity: 0; }
  }

  /* &caretdebug=1: wide red caret. Visible here but not normally = colour, not painting. */
  .mobile-editor--caretdebug .public-DraftEditor-content[contenteditable='true'] {
    caret-color: #ff0000 !important;
  }
  .mobile-editor__caretdebug {
    position: fixed;
    top: calc(var(--vvtop, 0px) + 2px);
    left: 2px;
    right: 2px;
    z-index: 4000;
    padding: 3px 5px;
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.82);
    color: #7CFC00;
    font-family: monospace;
    font-size: 10px;
    line-height: 1.3;
    pointer-events: none;
    white-space: pre-wrap;
  }

  .mobile-editor__save-error {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    z-index: 3100;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background-color: #fdecea;
    border: 1px solid #f5c2c0;
    color: #7f1d1d;
    font-size: 14px;
    line-height: 1.35;
  }
  .mobile-editor__save-error button {
    margin-left: auto;
    flex-shrink: 0;
    min-height: 32px;
    padding: 0 10px;
    border: 1px solid #f5c2c0;
    border-radius: 8px;
    background-color: #ffffff;
    color: #7f1d1d;
    font-size: 13px;
  }

  /* Video atomic block rendered INLINE like an image: the video's first
     frame with a centered play badge, capped to the editor width. */
  .mobile-editor__video-inline {
    position: relative;
    touch-action: manipulation;
    /* display:table shrink-wraps like inline-block but lets margin:auto
       center the block on its own line - same treatment images get. */
    display: table;
    max-width: 100%;
    margin: 6px auto;
    border-radius: 2px;
    cursor: pointer;
  }
  .mobile-editor__video-inline-frame {
    display: block;
    max-width: 100%;
    max-height: 260px;
    border-radius: 2px;
    background-color: #000000;
    /* Taps must land on the wrapper (select / open the dialog), never on the
       element itself - no accidental inline playback or native controls. */
    pointer-events: none;
  }
  .mobile-editor__video-inline-play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding-left: 3px; /* optically center the triangle */
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.55);
    color: #ffffff;
    font-size: 16px;
    pointer-events: none;
    box-sizing: border-box;
  }
  /* 16:9 placeholder + spinner until the first frame decodes. */
  .mobile-editor__video-inline--pending {
    width: 320px;
    max-width: 100%;
    height: 180px;
  }
  .mobile-editor__video-inline--pending .mobile-editor__video-inline-frame {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background-color: #e9edf1;
  }
  .mobile-editor__video-inline-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 28px;
    height: 28px;
    margin: -14px 0 0 -14px;
    border: 3px solid #d0d7de;
    border-top-color: #00a0e1;
    border-radius: 50%;
    animation: mobile-editor-spin 0.8s linear infinite;
    pointer-events: none;
    box-sizing: border-box;
  }
  /* Single-tap selection ring - same treatment as a selected image
     (see handleContainerPointerUp / the applySelection effect). */
  .mobile-editor__video-inline.wwa-image-selected {
    outline: 3px solid #00a0e1;
    outline-offset: -3px;
  }

  .mobile-editor__wrapper figure[data-block='true'],
  .mobile-editor__wrapper figure[data-block='true'] * {
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }

  /* Toolbar "insert video" button: a real <input type=file> behind a label,
     styled to match the other rdw-option-wrapper icon buttons. */
  .mobile-editor__video-toolbar-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .mobile-editor__video-toolbar-label {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 16px;
    color: #4a4a4a; /* matches the library's grey toolbar icons */
    cursor: pointer;
  }
  .mobile-editor__toolbar-icon {
    display: block;
  }
  /* Rendered, not display:none - programmatic .click() needs that on iOS. Invisible, inert. */
  .mobile-editor__hidden-file-input {
    position: fixed;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
    font-size: 16px;
  }

  /* Full-screen video player opened by tapping an attachment card */
  .mobile-editor__video-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.85);
    z-index: 4000;
    box-sizing: border-box;
    padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px)
      env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
  }
  .mobile-editor__video-player {
    max-width: 100vw;
    max-height: 100vh;
    max-width: 100dvw;
    max-height: 100dvh;
  }

  /* Custom popups (colour / link / image-by-URL): fixed to the top of the visible viewport. */
  .mobile-editor__popup-backdrop {
    position: fixed;
    inset: 0;
    z-index: 4000;
    background-color: rgba(0, 0, 0, 0.25);
  }
  .mobile-editor__popup {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 12px);
    left: 50%;
    transform: translateX(-50%) translateZ(0);
    width: min(340px, 92vw);
    max-height: calc(
      var(--vvh, 100vh) - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px
    );
    overflow-y: auto;
    box-sizing: border-box;
    padding: 16px;
    background-color: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.2);
  }
  .mobile-editor__popup-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
  }
  /* Picker popups (font / size / paragraph style). Under &toolbar=native the
     library's dropdowns are gone with the toolbar that owned them, so these are
     how those three controls stay reachable. */
  .mobile-editor__choice-list {
    display: flex;
    flex-direction: column;
    max-height: 50vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: auto;
    margin: 0 -4px;
  }
  .mobile-editor__choice {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #1a1a1a;
    font-size: 15px;
    text-align: left;
    cursor: pointer;
    touch-action: manipulation;
  }
  .mobile-editor__choice:hover {
    background-color: #f2f4f7;
  }
  .mobile-editor__choice--active {
    background-color: #e6f0f5;
    font-weight: 600;
  }

  .mobile-editor__color-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }
  .mobile-editor__color-swatch {
    width: 100%;
    aspect-ratio: 1 / 1;
    min-height: 40px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    padding: 0;
    cursor: pointer;
  }
  .mobile-editor__popup-input {
    width: 100%;
    height: 44px;
    box-sizing: border-box;
    padding: 0 12px;
    font-size: 16px; /* >=16px stops iOS from zooming the page on focus */
    color: #1a1a1a;
    border: 1px solid #d0d7de;
    border-radius: 8px;
    outline: none;
  }
  .mobile-editor__popup-input:focus {
    border-color: #00a0e1;
  }
  .mobile-editor__popup-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 14px;
  }
  .mobile-editor__popup-btn {
    height: 40px;
    padding: 0 16px;
    font-size: 15px;
    border: 1px solid #d0d7de;
    border-radius: 8px;
    background-color: #ffffff;
    color: #1a1a1a;
    cursor: pointer;
  }
  .mobile-editor__popup-btn--primary {
    border-color: #00a0e1;
    background-color: #00a0e1;
    color: #ffffff;
  }
  .mobile-editor__popup-btn--ghost {
    margin-right: auto;
    border-color: transparent;
    color: #d14841;
  }

  .mobile-editor,
  .mobile-editor__area,
  .mobile-editor__wrapper,
  .mobile-editor__editor {
    background-color: #ffffff;
  }

  /* Page = visible viewport (above keyboard). No body scroll → no tap jumps. */
  /* Document does not scroll by default - .rdw-editor-main does. */
  /* html/body sizing comes from JS: it differs between default and &scroll=page. */
  #root {
    min-height: 100%;
  }

  .mobile-editor {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100vw;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    /* No touch-action here: it intersects down the ancestor chain and would kill
       double-tap word select. */
    /* contain: layout gives the fixed popups a containing block without the layer
       promotion a transform causes - that promotion breaks caret painting on iOS. */
    contain: layout;
  }

  /* Fallback for engines without CSS containment: keep the original behaviour
     rather than letting the popups escape to the layout viewport. */
  @supports not (contain: layout) {
    .mobile-editor {
      transform: translate(0);
    }
  }

  /* &scroll=page: document scrolls, no inner overflow box. */
  .mobile-editor--pagescroll {
    transform: none;
    -webkit-transform: none;
    position: static;
    height: auto !important;
    min-height: var(--vvh, 100vh);
    overflow: visible;
    contain: none;
  }
  .mobile-editor--pagescroll .mobile-editor__area,
  .mobile-editor--pagescroll .mobile-editor__wrapper {
    display: block;
    min-height: 0;
  }
  .mobile-editor--pagescroll .mobile-editor__wrapper .rdw-editor-main {
    overflow: visible;
    flex: none;
    min-height: 0;
    position: static;
    z-index: auto;
    margin-top: calc(-1 * var(--toolbar-h, 48px));
  }

  .mobile-editor--ios.mobile-editor--pagescroll .mobile-editor__area,
  .mobile-editor--ios.mobile-editor--pagescroll .mobile-editor__wrapper,
  .mobile-editor--ios.mobile-editor--pagescroll .rdw-editor-main,
  .mobile-editor--ios.mobile-editor--pagescroll .DraftEditor-root,
  .mobile-editor--ios.mobile-editor--pagescroll .DraftEditor-editorContainer,
  .mobile-editor--ios.mobile-editor--pagescroll .public-DraftEditor-content {
    overflow: visible !important;
    transform: none !important;
    -webkit-transform: none !important;
    will-change: auto !important;
    contain: none !important;
    perspective: none !important;
    -webkit-perspective: none !important;
    -webkit-backface-visibility: visible !important;
    backface-visibility: visible !important;
  }
  .mobile-editor--pagescroll .mobile-editor__toolbar {
    position: -webkit-sticky;
    position: sticky;
    left: 0;
    right: 0;
    top: 0;
    bottom: auto;
    z-index: 100;
    -webkit-transform: translate3d(
      0,
      calc(var(--vvh, 100vh) - var(--toolbar-h, 48px)),
      0
    );
    transform: translate3d(
      0,
      calc(var(--vvh, 100vh) - var(--toolbar-h, 48px)),
      0
    );
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
  .mobile-editor--pagescroll .mobile-editor__toolbar .rdw-dropdown-optionwrapper,
  .mobile-editor--pagescroll .mobile-editor__toolbar .rdw-colorpicker-modal,
  .mobile-editor--pagescroll .mobile-editor__toolbar .rdw-link-modal,
  .mobile-editor--pagescroll .mobile-editor__toolbar .rdw-image-modal {
    top: calc(
      var(--toolbar-h, 48px) - var(--vvh, 100vh) +
        env(safe-area-inset-top, 0px) + 12px
    );
  }
  /* Room to scroll the last lines clear of the pinned toolbar. Only the
     toolbar: the keyboard's space is already gone from --vvh. */
  .mobile-editor--pagescroll .mobile-editor__wrapper .public-DraftEditor-content {
    padding-bottom: calc(var(--toolbar-h, 48px) + 2em);
  }

  /* &keyboard=host: the app keeps the web view above the keyboard, so the layout
     viewport is the visible area and a plain fixed footer lands on the keyboard.
     Must stay before the native-toolbar block, which wins ties when both apply. */
  .mobile-editor--host-keyboard .mobile-editor__toolbar {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    -webkit-transform: none;
    transform: none;
  }
  .mobile-editor--host-keyboard .mobile-editor__toolbar .rdw-dropdown-optionwrapper,
  .mobile-editor--host-keyboard .mobile-editor__toolbar .rdw-colorpicker-modal,
  .mobile-editor--host-keyboard .mobile-editor__toolbar .rdw-link-modal,
  .mobile-editor--host-keyboard .mobile-editor__toolbar .rdw-image-modal {
    top: calc(env(safe-area-inset-top, 0px) + 12px);
  }
  /* The toolbar is out of flow here, so nothing to pull the content up under. */
  .mobile-editor--host-keyboard .mobile-editor__wrapper .rdw-editor-main {
    margin-top: 0;
  }

  /* &toolbar=native: the host draws the toolbar outside the web view, so the
     page has none. It always implies --pagescroll, and it turns off the two
     rules above that exist purely to make room for an in-page toolbar.
     JS also pins --toolbar-h to 0 here, but only once its effect has run: these
     rules keep the first paint from flashing a -48px pull.

     The sticky-toolbar rule and its dropdown "top" compensation above are dead
     in this mode - .mobile-editor__toolbar is never rendered, and the library
     dropdowns are its DOM children, so they go with it. The colour / link /
     image-by-URL popups are siblings of .mobile-editor__area and survive; the
     native toolbar opens them through EditorBridge.command('openPopup', ...). */
  .mobile-editor--native-toolbar .mobile-editor__wrapper .rdw-editor-main {
    margin-top: 0;
  }
  /* A tail of slack so the last lines are not stranded against the frame edge.
     No --toolbar-h term: the host sized its frame above both the keyboard and
     its own toolbar, so every pixel of the page is already visible. */
  .mobile-editor--native-toolbar .mobile-editor__wrapper .public-DraftEditor-content {
    padding-bottom: calc(2em + env(safe-area-inset-bottom, 0px));
  }

  .mobile-editor__area {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* No touch-action - see .mobile-editor. This is an ancestor of the
       contenteditable, so "manipulation" here also removes double-tap. */
  }

  /* Editor wrapper: editor scrolls, toolbar pinned at the bottom via flex order. */
  .mobile-editor__wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  /* No -webkit-overflow-scrolling:touch, z-index 0: on iOS that layer paints
     above the toolbar popups. */
  .mobile-editor__wrapper .rdw-editor-main {
    order: 1;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    z-index: 0;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .mobile-editor__wrapper .rdw-editor-main::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }

  /* Contenteditable fills the whole area so a tap anywhere lands inside it. */
  .mobile-editor__editor {
    padding: 12px 14px;
    font-size: 16px;
    line-height: 1.6;
    color: #1a1a1a;
    min-height: 100%;
    /* No touch-action: double-tap selects a word here, triple-tap the block. */
  }
  .mobile-editor__wrapper .DraftEditor-root,
  .mobile-editor__wrapper .DraftEditor-editorContainer,
  .mobile-editor__wrapper .public-DraftEditor-content {
    min-height: 100%;
  }
  .mobile-editor__editor .public-DraftEditorPlaceholder-root {
    color: #9aa0a6;
  }
  /* Scroll slack so the keyboard cannot strand the last lines. --toolbar-h is
     measured in JS; vh resolves against the layout viewport and is useless here. */
  .mobile-editor__scroll-spacer {
    flex-shrink: 0;
    width: 100%;
    pointer-events: none;
  }
  .mobile-editor__wrapper .rdw-editor-main {
    scroll-padding-bottom: calc(var(--toolbar-h, 48px) + 24px);
  }
  /* Real padding, not just scroll-padding: scroll-padding adds no scrollable extent. */
  .mobile-editor__wrapper .public-DraftEditor-content {
    padding-bottom: calc(var(--toolbar-h, 48px) + 3em);
  }
  /* Edit mode = I-beam text cursor over the editable area, not the hand. */
  .mobile-editor__wrapper .public-DraftEditor-content[contenteditable='true'] {
    cursor: text;
    /* Name the caret colour: iOS renders it transparent inside a composited ancestor. */
    caret-color: #1a1a1a;
    -webkit-text-fill-color: currentColor;
  }

  /* Toolbar: one scrollable row. position + z-index lift its popups above the editor area. */
  .mobile-editor__toolbar {
    order: 2;
    flex: 0 0 auto;
    position: relative;
    z-index: 100;
    display: flex !important;
    align-items: center;
    flex-wrap: nowrap !important;
    gap: 4px;
    border: none !important;
    border-top: 1px solid #ececec !important;
    background-color: #ffffff !important;
    margin: 0 !important;
    padding: 8px calc(10px + env(safe-area-inset-right, 0px))
      calc(8px + env(safe-area-inset-bottom, 0px))
      calc(10px + env(safe-area-inset-left, 0px)) !important;
    overflow-x: auto;
    overflow-y: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.06);
  }
  .mobile-editor__toolbar::-webkit-scrollbar {
    display: none;
    height: 0;
  }

  /* Keyboard covers the home indicator, so the safe-area reservation is dead space.
       Two classes + !important to beat the padding above. */
  .mobile-editor--keyboard-open .mobile-editor__toolbar {
    padding-bottom: 8px !important;
  }

  /* Lift the overflow clip while a popup is open: iOS clips fixed descendants of this scroller. */
  .mobile-editor--ios.mobile-editor--popup-open .mobile-editor__toolbar {
    overflow: visible;
  }

  /* Each control group: single row, centered, no wrapping or stray margins */
  .mobile-editor__toolbar > div {
    display: flex !important;
    align-items: center;
    flex-wrap: nowrap !important;
    flex-shrink: 0;
    margin: 0 !important;
    padding: 0 !important;
    position: relative;
  }

  /* Thin divider between groups for a clean grouped look */
  .mobile-editor__toolbar > div:not(:last-child)::after {
    content: '';
    width: 1px;
    align-self: stretch;
    margin: 4px 6px;
    background-color: #ececec;
  }

  /* History (undo/redo) used to be pinned here with position:sticky + an opaque
     white background, so the rest of the toolbar scrolled UNDER it. The toolbar
     is ~1410px of controls in a ~375px viewport, so that pin did not keep
     undo/redo reachable so much as eat whatever scrolled beneath it: past about
     200px of scroll the font-family dropdown slid behind the pinned block and
     disappeared completely, which read as "there is no font family, I cannot
     change the font at all". Nothing may cover another control in a scroller
     this over-subscribed - history now scrolls with everything else and keeps
     the normal group divider. */

  /* Toolbar controls: minimal, evenly spaced, no boxed borders */
  .mobile-editor__toolbar .rdw-option-wrapper,
  .mobile-editor__toolbar .rdw-dropdown-wrapper {
    /* Only place with touch-action: buttons hold no selectable text, and it kills the tap delay. */
    touch-action: manipulation;
    flex-shrink: 0;
    border: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
    min-width: 30px;
    height: 32px;
    margin: 0 2px !important;
    padding: 0 5px !important;
    border-radius: 8px;
  }
  .mobile-editor__toolbar .rdw-option-wrapper:hover,
  .mobile-editor__toolbar .rdw-dropdown-wrapper:hover {
    background-color: #f2f4f7 !important;
    box-shadow: none !important;
  }
  .mobile-editor__toolbar .rdw-option-active {
    background-color: #e6f0f5 !important;
    box-shadow: none !important;
  }
  .mobile-editor__toolbar .rdw-option-disabled {
    opacity: 0.45;
  }
  .mobile-editor__toolbar .rdw-dropdown-wrapper {
    height: 32px;
    min-width: 96px;
    padding: 0 6px !important;
  }
  .mobile-editor__toolbar .rdw-dropdown-selectedtext {
    color: #1a1a1a;
    right: 22px;
    left: 8px;
  }

  /* All popups: fixed panels at the top of the visible viewport, clear of the keyboard. */
  .mobile-editor__toolbar .rdw-dropdown-optionwrapper,
  .mobile-editor__toolbar .rdw-colorpicker-modal,
  .mobile-editor__toolbar .rdw-link-modal,
  .mobile-editor__toolbar .rdw-image-modal {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 12px);
    left: 50%;
    /* translateZ: own compositor layer, so the scroller cannot paint over it. */
    transform: translateX(-50%) translateZ(0);
    bottom: auto;
    right: auto;
    /* Dropdowns shrink to content; the form modals below set a real width. */
    width: max-content;
    min-width: 112px;
    max-width: 92vw;
    height: auto;
    max-height: calc(
      var(--vvh, 100vh) - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px
    );
    overflow-y: auto;
    margin: 0;
    background-color: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.20);
    z-index: 2000;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .mobile-editor__toolbar .rdw-dropdown-optionwrapper::-webkit-scrollbar,
  .mobile-editor__toolbar .rdw-colorpicker-modal::-webkit-scrollbar,
  .mobile-editor__toolbar .rdw-link-modal::-webkit-scrollbar,
  .mobile-editor__toolbar .rdw-image-modal::-webkit-scrollbar {
    display: none;
    width: 0;
  }

  /* Form modals (link / image) need a real column width for their inputs. */
  .mobile-editor__toolbar .rdw-link-modal,
  .mobile-editor__toolbar .rdw-image-modal {
    width: min(320px, 92vw);
  }

  /* Dropdown list items */
  .mobile-editor__toolbar .rdw-dropdownoption-default {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 10px 16px;
    color: #1a1a1a;
    font-size: 15px;
  }

  /* Font-size list: a narrow, centered column of numbers. */
  .mobile-editor__toolbar .rdw-fontsize-dropdown .rdw-dropdown-optionwrapper {
    min-width: 88px;
  }
  .mobile-editor__toolbar .rdw-fontsize-dropdown .rdw-dropdownoption-default {
    justify-content: center;
    padding: 10px 12px;
  }
  .mobile-editor__toolbar .rdw-dropdownoption-active,
  .mobile-editor__toolbar .rdw-dropdownoption-highlighted {
    background-color: #e6f0f5;
  }

  /* Modals get inner padding (dropdown lists do not) */
  .mobile-editor__toolbar .rdw-colorpicker-modal,
  .mobile-editor__toolbar .rdw-link-modal,
  .mobile-editor__toolbar .rdw-image-modal {
    padding: 14px;
    box-sizing: border-box;
  }

  /* Color picker swatch grid */
  .mobile-editor__toolbar .rdw-colorpicker-modal-options {
    margin: 6px 0 0;
    overflow-y: auto;
  }
  .mobile-editor__toolbar .rdw-colorpicker-option {
    margin: 3px;
    box-shadow: none;
  }

  /* Inputs inside link/image modals: comfortable tap targets */
  .mobile-editor__toolbar .rdw-link-modal-input,
  .mobile-editor__toolbar .rdw-image-modal-url-input,
  .mobile-editor__toolbar .rdw-image-modal-size-input {
    height: 40px;
    box-sizing: border-box;
    color: #1a1a1a;
    margin: 0;
  }
  .mobile-editor__toolbar .rdw-link-modal {
    height: auto;
  }

  /* Image modal: even, full-width layout */
  .mobile-editor__toolbar .rdw-image-modal-header {
    display: flex;
    width: 100%;
    margin-bottom: 10px;
  }
  .mobile-editor__toolbar .rdw-image-modal-header-option {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
  }
  .mobile-editor__toolbar .rdw-image-modal-upload-option,
  .mobile-editor__toolbar .rdw-image-modal-url-section {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    margin: 0;
  }
  .mobile-editor__toolbar .rdw-image-modal-url-input {
    flex: 1;
    width: 100%;
  }
  .mobile-editor__toolbar .rdw-image-modal-size {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    margin-top: 10px;
  }
  .mobile-editor__toolbar .rdw-image-modal-size-input {
    flex: 1;
    width: auto;
    min-width: 0;
  }

  .mobile-editor__toolbar .rdw-link-modal-buttonsection,
  .mobile-editor__toolbar .rdw-image-modal-btn-section {
    display: flex;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin-top: 12px;
  }
  .mobile-editor__toolbar .rdw-link-modal-btn,
  .mobile-editor__toolbar .rdw-image-modal-btn {
    height: 36px;
    margin: 0;
    border-radius: 8px;
  }
`;
